import { useEffect, useRef } from 'react'
import type * as THREE from 'three'

const LoadingState = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const sceneRef = useRef<THREE.Scene | null>(null)
  const rendererRef = useRef<THREE.WebGLRenderer | null>(null)
  const composerRef = useRef<{
    render: () => void
    setSize: (w: number, h: number) => void
  } | null>(null)
  const animationIdRef = useRef<number | undefined>(undefined)

  useEffect(() => {
    const generatePathPoints = (
      THREE: typeof import('three'),
      count = 10,
      spacing = 25
    ) => {
      const points = []
      for (let i = 0; i < count; i++) {
        const x = i * spacing
        const y = Math.sin(i * 0.5 + Math.random()) * 100 + 50
        const z = Math.cos(i * 0.3 + Math.random()) * 100 + 50
        points.push(new THREE.Vector3(x, z, y))
      }
      return points
    }

    const createMaterials = (THREE: typeof import('three')) => {
      const ringMaterial = new THREE.LineBasicMaterial({
        color: 0x8b5cf6, // Purple
        transparent: true,
        opacity: 0.8,
        depthWrite: false,
      })

      const ringMaterial1 = new THREE.LineBasicMaterial({
        color: 0xa855f7, // Lighter purple
        transparent: true,
        opacity: 0.6,
        depthWrite: false,
      })

      const ringMaterial2 = new THREE.LineBasicMaterial({
        color: 0xc084fc, // Even lighter purple
        transparent: true,
        opacity: 0.4,
        depthWrite: false,
      })

      return { ringMaterial, ringMaterial1, ringMaterial2 }
    }

    interface RingConfig {
      THREE: typeof import('three')
      scene: THREE.Scene
      path: THREE.CatmullRomCurve3
      ringCount: number
      ringRadius: number
      ringSegments: number
      materials: ReturnType<typeof createMaterials>
    }

    const createRings = (config: RingConfig) => {
      const {
        THREE,
        scene,
        path,
        ringCount,
        ringRadius,
        ringSegments,
        materials,
      } = config
      const frenetFrames = path.computeFrenetFrames(ringCount, true)

      for (let i = 0; i <= ringCount; i++) {
        const t = i / ringCount
        const pos = path.getPointAt(t)
        const normal = frenetFrames.normals[i]
        const binormal = frenetFrames.binormals[i]

        const ringPoints = []
        for (let j = 0; j <= ringSegments; j++) {
          const theta = (j / ringSegments) * Math.PI * 2
          const x = Math.cos(theta) * ringRadius
          const y = Math.sin(theta) * ringRadius

          const point = new THREE.Vector3().addVectors(
            pos,
            new THREE.Vector3()
              .addScaledVector(normal, x)
              .addScaledVector(binormal, y)
          )

          ringPoints.push(point)
        }

        const ringGeometry = new THREE.BufferGeometry().setFromPoints(
          ringPoints
        )
        let material = materials.ringMaterial

        // Alternate materials for variety
        if (i % 3 === 0) material = materials.ringMaterial1
        else if (i % 3 === 1) material = materials.ringMaterial2

        const ringMesh = new THREE.LineLoop(ringGeometry, material)
        scene.add(ringMesh)
      }
    }

    interface PostProcessingConfig {
      THREE: typeof import('three')
      scene: THREE.Scene
      camera: THREE.PerspectiveCamera
      renderer: THREE.WebGLRenderer
      w: number
      h: number
      EffectComposer: typeof import('three/examples/jsm/postprocessing/EffectComposer.js').EffectComposer
      RenderPass: typeof import('three/examples/jsm/postprocessing/RenderPass.js').RenderPass
      UnrealBloomPass: typeof import('three/examples/jsm/postprocessing/UnrealBloomPass.js').UnrealBloomPass
    }

    const setupPostProcessing = (config: PostProcessingConfig) => {
      const {
        THREE,
        scene,
        camera,
        renderer,
        w,
        h,
        EffectComposer,
        RenderPass,
        UnrealBloomPass,
      } = config
      const renderScene = new RenderPass(scene, camera)
      const bloomPass = new UnrealBloomPass(
        new THREE.Vector2(w, h),
        2.0, // Increased bloom
        0.4,
        0.85
      )
      bloomPass.renderToScreen = true

      const composer = new EffectComposer(renderer)
      composer.setSize(w, h)
      composer.addPass(renderScene)
      composer.addPass(bloomPass)
      composerRef.current = composer

      return composer
    }

    const setupRenderer = (
      THREE: typeof import('three'),
      canvas: HTMLCanvasElement
    ) => {
      const w = window.innerWidth
      const h = window.innerHeight

      const renderer = new THREE.WebGLRenderer({
        canvas,
        antialias: true,
        alpha: true,
      })
      renderer.setSize(w, h)
      renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
      rendererRef.current = renderer

      return { renderer, w, h }
    }

    const setupScene = (THREE: typeof import('three')) => {
      const scene = new THREE.Scene()
      scene.background = new THREE.Color(0x1a0b2e) // Dark purple background
      sceneRef.current = scene
      return scene
    }

    const setupCamera = (
      THREE: typeof import('three'),
      w: number,
      h: number,
      scene: THREE.Scene
    ) => {
      const camera = new THREE.PerspectiveCamera(45, w / h, 0.001, 200)
      const cameraRotationProxyX = Math.PI
      const cameraRotationProxyY = 0
      camera.rotation.y = cameraRotationProxyX
      camera.rotation.z = cameraRotationProxyY

      const cameraGroup = new THREE.Group()
      cameraGroup.position.z = 400
      cameraGroup.add(camera)
      scene.add(cameraGroup)

      return { camera, cameraGroup, cameraRotationProxyX, cameraRotationProxyY }
    }

    const setupGeometry = (
      THREE: typeof import('three'),
      scene: THREE.Scene
    ) => {
      const points = generatePathPoints(THREE, 10)
      const path = new THREE.CatmullRomCurve3(points)
      path.closed = true
      path.tension = 1

      const ringCount = 600
      const ringRadius = 3
      const ringSegments = 32

      const geometry = new THREE.TubeGeometry(
        path,
        ringCount,
        ringRadius,
        ringSegments,
        true
      )

      const wireframe = new THREE.LineSegments(
        new THREE.EdgesGeometry(geometry),
        new THREE.LineBasicMaterial({
          color: 0x8b5cf6,
          linewidth: 0.1,
          opacity: 0.1,
        })
      )
      scene.add(wireframe)

      const materials = createMaterials(THREE)
      createRings({
        THREE,
        scene,
        path,
        ringCount,
        ringRadius,
        ringSegments,
        materials,
      })

      return { path, ringCount, ringRadius, ringSegments }
    }

    const setupLighting = (
      THREE: typeof import('three'),
      scene: THREE.Scene
    ) => {
      const light = new THREE.PointLight(0xa855f7, 0.3, 4, 0)
      light.castShadow = true
      scene.add(light)
      return light
    }

    const initThreeJS = async () => {
      const THREE = await import('three')
      const { EffectComposer } = await import(
        'three/examples/jsm/postprocessing/EffectComposer.js'
      )
      const { RenderPass } = await import(
        'three/examples/jsm/postprocessing/RenderPass.js'
      )
      const { UnrealBloomPass } = await import(
        'three/examples/jsm/postprocessing/UnrealBloomPass.js'
      )

      const canvas = canvasRef.current
      if (!canvas) return

      const { renderer, w, h } = setupRenderer(THREE, canvas)
      const scene = setupScene(THREE)
      const {
        camera,
        cameraGroup,
        cameraRotationProxyX,
        cameraRotationProxyY,
      } = setupCamera(THREE, w, h, scene)
      const { path } = setupGeometry(THREE, scene)
      const light = setupLighting(THREE, scene)

      const composer = setupPostProcessing({
        THREE,
        scene,
        camera,
        renderer,
        w,
        h,
        EffectComposer,
        RenderPass,
        UnrealBloomPass,
      })

      let cameraTargetPercentage = 0

      function updateCameraPercentage(percentage: number) {
        const p1 = path.getPointAt(percentage % 1)
        const p2 = path.getPointAt((percentage + 0.01) % 1)
        cameraGroup.position.set(p1.x, p1.y, p1.z)
        cameraGroup.lookAt(p2)
        light.position.set(p2.x, p2.y, p2.z)
      }

      function render() {
        cameraTargetPercentage = (cameraTargetPercentage + 0.002) % 1
        camera.rotation.y += (cameraRotationProxyX - camera.rotation.y) / 15
        camera.rotation.x += (cameraRotationProxyY - camera.rotation.x) / 15
        updateCameraPercentage(cameraTargetPercentage)
        composer.render()
        animationIdRef.current = requestAnimationFrame(render)
      }

      render()

      const handleResize = () => {
        const newW = window.innerWidth
        const newH = window.innerHeight
        camera.aspect = newW / newH
        camera.updateProjectionMatrix()
        renderer.setSize(newW, newH)
        composer.setSize(newW, newH)
      }

      window.addEventListener('resize', handleResize)

      return () => {
        window.removeEventListener('resize', handleResize)
        if (animationIdRef.current) {
          cancelAnimationFrame(animationIdRef.current)
        }
        if (renderer) {
          renderer.dispose()
        }
      }
    }

    initThreeJS()

    // Cleanup on unmount
    return () => {
      if (animationIdRef.current) {
        cancelAnimationFrame(animationIdRef.current)
      }
    }
  }, [])

  return (
    <div className="fixed inset-0 z-50">
      <canvas
        ref={canvasRef}
        className="absolute inset-0 w-full h-full"
        style={{ zIndex: 2 }}
      />
    </div>
  )
}

export default LoadingState
